import React, { useState } from "react";
import { FiDownload } from "react-icons/fi";

const compColor = (val, limit) => {
  if (val > limit) return "#ef4444";
  if (val > limit * 0.85) return "#f59e0b";
  return "#10b981";
};

const AqmCompliancePanel = ({ d }) => {
  const [activeView, setActiveView] = useState("Daily Log");
  const { compliance } = d;

  return (
    <div className="aqm-compliance-panel">
      <div className="aqm-comp-header">
        <div>
          <div className="aqm-section-title">Compliance & CPCB Reporting</div>
          <div className="aqm-comp-authority">{compliance.authority} · {compliance.standard}</div>
          <div className="aqm-comp-authority" style={{ fontSize: 11 }}>Permit: {compliance.permitId}</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div className={`aqm-comp-status-badge ${compliance.violations > 0 ? "aqm-comp-fail" : "aqm-comp-ok"}`}>
            {compliance.violations > 0 ? `❌ ${compliance.violations} Violations` : "✅ Compliant"}
          </div>
          <button className="aqm-ctrl-btn aqm-ctrl-purple" style={{ display: "flex", gap: 5, alignItems: "center" }}>
            <FiDownload size={13} /> Export CPCB Report
          </button>
        </div>
      </div>

      {/* Audit info */}
      <div className="aqm-comp-audit-row">
        <div className="aqm-ca-item"><label>Last Audit</label><span>{compliance.lastAuditDate}</span></div>
        <div className="aqm-ca-item"><label>Next Audit</label><span>{compliance.nextAuditDate}</span></div>
        <div className="aqm-ca-item"><label>Violations (30d)</label>
          <span style={{ color: compliance.violations > 0 ? "#ef4444" : "#10b981", fontWeight: 700 }}>{compliance.violations}</span>
        </div>
        <div className="aqm-ca-item"><label>Standard</label><span>{compliance.standard}</span></div>
      </div>

      <div className="aqm-view-row">
        {["Daily Log","Violation History","NAAQS Limits"].map(v => (
          <button key={v} className={`aqm-view-btn ${activeView === v ? "aqm-view-active" : ""}`} onClick={() => setActiveView(v)}>{v}</button>
        ))}
      </div>

      {/* Daily Log */}
      {activeView === "Daily Log" && (
        <div className="aqm-chart-card">
          <div className="aqm-section-title">Daily 24-Hour Average — Compliance Log</div>
          <table className="aqm-table">
            <thead>
              <tr><th>Date</th><th>PM2.5</th><th>PM10</th><th>SO₂</th><th>NO₂</th><th>CO</th><th>O₃</th><th>Status</th></tr>
            </thead>
            <tbody>
              {compliance.dailyLog.map((row, i) => (
                <tr key={i} className={!row.compliant ? "aqm-tr-violation" : ""}>
                  <td style={{ fontSize: 12 }}>{row.date}</td>
                  <td style={{ color: compColor(row.pm25, 60), fontWeight: 600 }}>{row.pm25}</td>
                  <td style={{ color: compColor(row.pm10, 100), fontWeight: 600 }}>{row.pm10}</td>
                  <td style={{ color: compColor(row.so2, 80), fontWeight: 600 }}>{row.so2}</td>
                  <td style={{ color: compColor(row.no2, 80), fontWeight: 600 }}>{row.no2}</td>
                  <td style={{ color: compColor(row.co, 2), fontWeight: 600 }}>{row.co}</td>
                  <td style={{ color: compColor(row.o3, 100), fontWeight: 600 }}>{row.o3}</td>
                  <td>
                    <span className="aqm-badge" style={{ background: row.compliant ? "#d1fae5" : "#fee2e2", color: row.compliant ? "#10b981" : "#ef4444" }}>
                      {row.compliant ? "✅ OK" : "❌ Breach"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8 }}>Units: PM2.5, PM10, SO₂, NO₂, O₃ in µg/m³ · CO in mg/m³</div>
        </div>
      )}

      {/* Violation History */}
      {activeView === "Violation History" && (
        <div className="aqm-chart-card">
          <div className="aqm-section-title">NAAQS Violation Log (Last 30 Days)</div>
          {compliance.violationHistory.length === 0 ? (
            <div className="aqm-empty">No violations in the last 30 days 🎉</div>
          ) : compliance.violationHistory.map((v, i) => (
            <div key={i} className="aqm-violation-row">
              <div className="aqm-vr-date">{v.date}</div>
              <div className="aqm-vr-param">{v.parameter}</div>
              <div className="aqm-vr-val">Measured: <strong style={{ color: "#ef4444" }}>{v.value}</strong></div>
              <div className="aqm-vr-limit">NAAQS Limit: {v.limit}</div>
              <div className="aqm-vr-station">Station: <strong>{v.station}</strong></div>
              <div className="aqm-vr-action">Action: {v.action}</div>
            </div>
          ))}
        </div>
      )}

      {/* NAAQS Limits */}
      {activeView === "NAAQS Limits" && (
        <div className="aqm-chart-card">
          <div className="aqm-section-title">Current Averages vs NAAQS 2009 Limits</div>
          <table className="aqm-table">
            <thead>
              <tr><th>Parameter</th><th>Current 24h Avg</th><th>NAAQS Limit</th><th>% of Limit</th><th>Status</th></tr>
            </thead>
            <tbody>
              {[
                { label: "PM2.5",  val: compliance.currentAverages.pm25, limit: compliance.limits.pm25_24h,  unit: "µg/m³" },
                { label: "PM10",   val: compliance.currentAverages.pm10, limit: compliance.limits.pm10_24h,  unit: "µg/m³" },
                { label: "SO₂",   val: compliance.currentAverages.so2,  limit: compliance.limits.so2_24h,   unit: "µg/m³" },
                { label: "NO₂",   val: compliance.currentAverages.no2,  limit: compliance.limits.no2_24h,   unit: "µg/m³" },
                { label: "CO",     val: compliance.currentAverages.co,   limit: compliance.limits.co_8h,     unit: "mg/m³" },
                { label: "O₃",    val: compliance.currentAverages.o3,   limit: compliance.limits.o3_8h,     unit: "µg/m³" },
              ].map(r => {
                const col = compColor(r.val, r.limit);
                const pct = ((r.val / r.limit) * 100).toFixed(0);
                return (
                  <tr key={r.label}>
                    <td style={{ fontWeight: 600 }}>{r.label}</td>
                    <td style={{ fontWeight: 700, color: col }}>{r.val} {r.unit}</td>
                    <td style={{ color: "#6b7280" }}>{r.limit} {r.unit}</td>
                    <td style={{ color: col, fontWeight: 600 }}>{pct}%</td>
                    <td><span className="aqm-badge" style={{ background: col + "18", color: col, fontSize: 11 }}>{col === "#10b981" ? "OK" : col === "#f59e0b" ? "Near Limit" : "BREACH"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AqmCompliancePanel;
